import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isAiNotesEnabled, isAiNotesPanelVisible } from '../../../ai-notes/functions';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { IconAI, IconChatBubbleToggle, IconFaceSmile, IconSend, IconShareDoc } from '../../../base/icons/svg';
import { sendMessage, toggleChat } from '../../../chat/actions.web';
import SmileysPanel from '../../../chat/components/web/SmileysPanel';
import { CHAR_LIMIT } from '../../../chat/constants';
import { areSmileysDisabled, getUnreadCount, isChatDisabled, isSendGroupChatDisabled } from '../../../chat/functions';
import { toggleSidePanelApp } from '../../../side-panel/actions';
import { SidePanelTabId } from '../../../side-panel/constants';
import { isSidePanelOpen } from '../../../side-panel/functions';
import { closeOverflowMenuIfOpen, setToolbarHovered, setToolboxVisible } from '../../actions.web';
import logger from '../../logger';
import { truncatePreviewText, useQuickChatPreviews } from './useQuickChatPreviews';
/**
 * 手机/窄屏底栏上方的悬浮快捷入口：左侧气泡聊天开关、右侧 AI 纪要圆钮.
 * 关闭时为圆形开关；打开后展开胶囊（开关 | 表情 | 输入）并显示消息气泡.
 *
 * @returns {ReactElement|null}
 */
const MobileToolboxShortcuts = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const chatDisabled = useSelector(isChatDisabled);
    const chatOpen = useSelector((state) => Boolean(state['features/chat'].isOpen));
    const aiNotesEnabled = useSelector(isAiNotesEnabled);
    const aiNotesOpen = useSelector(isAiNotesPanelVisible);
    const sidePanelOpen = useSelector(isSidePanelOpen);
    const sendGroupChatDisabled = useSelector(isSendGroupChatDisabled);
    const smileysDisabled = useSelector(areSmileysDisabled);
    const messages = useSelector((state) => state['features/chat'].messages);
    const unreadCount = useSelector((state) => getUnreadCount(state));
    const [draft, setDraft] = useState('');
    const [showSmileysPanel, setShowSmileysPanel] = useState(false);
    const [bubbleChatOpen, setBubbleChatOpen] = useState(false);
    const { bumpPreviewTick, renderedPreviews } = useQuickChatPreviews(messages);
    const showChat = !chatDisabled;
    const showAiNotes = aiNotesEnabled;
    const hideForDrawer = sidePanelOpen || chatOpen || aiNotesOpen;
    const inputDisabled = sendGroupChatDisabled;
    const canSend = !inputDisabled && Boolean(draft.trim());
    const keepToolboxVisible = useCallback(() => {
        dispatch(setToolboxVisible(true));
        dispatch(setToolbarHovered(true));
    }, [dispatch]);
    const handleToggleBubbleChat = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setBubbleChatOpen(prev => {
            const next = !prev;
            logger.info(`手机底栏悬浮入口 | 气泡聊天 ${next ? '打开' : '关闭'}`);
            sendAnalytics(createToolbarEvent('toggle.bubble.chat', {
                enable: next,
                entry: 'mobile-shortcut'
            }));
            if (!next) {
                setShowSmileysPanel(false);
            }
            return next;
        });
        keepToolboxVisible();
    }, [keepToolboxVisible]);
    const handleOpenChat = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setShowSmileysPanel(false);
        logger.info(`手机底栏悬浮入口 | 打开聊天 unread=${unreadCount}`);
        sendAnalytics(createToolbarEvent('toggle.chat', {
            enable: true,
            entry: 'mobile-shortcut-preview'
        }));
        dispatch(closeOverflowMenuIfOpen());
        dispatch(toggleChat());
    }, [dispatch, unreadCount]);
    const handleOpenAiNotes = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setShowSmileysPanel(false);
        logger.info('手机底栏悬浮入口 | 打开AI纪要');
        sendAnalytics(createToolbarEvent('ainotes', {
            entry: 'mobile-shortcut'
        }));
        dispatch(closeOverflowMenuIfOpen());
        dispatch(toggleSidePanelApp(SidePanelTabId.AI_NOTES));
    }, [dispatch]);
    const handleSend = useCallback(() => {
        if (inputDisabled) {
            return;
        }
        const trimmed = draft.trim();
        if (!trimmed) {
            return;
        }
        logger.info(`手机底栏悬浮入口 | 发送聊天 length=${trimmed.length}`);
        dispatch(sendMessage(trimmed));
        setDraft('');
        setShowSmileysPanel(false);
        bumpPreviewTick();
        keepToolboxVisible();
    }, [bumpPreviewTick, dispatch, draft, inputDisabled, keepToolboxVisible]);
    const handleInputChange = useCallback((event) => {
        const nextValue = event.target.value;
        if (nextValue.length <= CHAR_LIMIT) {
            setDraft(nextValue);
        }
    }, []);
    const handleInputFocus = useCallback(() => {
        setShowSmileysPanel(false);
        keepToolboxVisible();
    }, [keepToolboxVisible]);
    const handleKeyDown = useCallback((event) => {
        if (event.nativeEvent.isComposing || event.keyCode === 229) {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            handleSend();
        }
        if (event.key === 'Escape' && showSmileysPanel) {
            event.preventDefault();
            setShowSmileysPanel(false);
        }
    }, [handleSend, showSmileysPanel]);
    const handlePreventMouseDown = useCallback((event) => {
        event.preventDefault();
    }, []);
    const handleToggleSmileys = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (inputDisabled || smileysDisabled) {
            logger.info('手机底栏悬浮入口 | 表情不可用');
            return;
        }
        setShowSmileysPanel(prev => {
            const next = !prev;
            logger.info(`手机底栏悬浮入口 | 表情面板 ${next ? '打开' : '关闭'}`);
            return next;
        });
        keepToolboxVisible();
    }, [inputDisabled, keepToolboxVisible, smileysDisabled]);
    const handleSmileySelect = useCallback((smileyText) => {
        if (smileyText) {
            setDraft(prev => {
                const next = `${prev}${prev ? ' ' : ''}${smileyText}`;
                return next.length <= CHAR_LIMIT ? next : prev;
            });
            logger.info('手机底栏悬浮入口 | 插入表情');
        }
        setShowSmileysPanel(false);
        keepToolboxVisible();
        inputRef.current?.focus();
    }, [keepToolboxVisible]);
    if (hideForDrawer || (!showChat && !showAiNotes)) {
        return null;
    }
    return (React.createElement("div", { className: 'mobile-toolbox-shortcuts' },
        showChat && !bubbleChatOpen && (React.createElement("button", { "aria-label": t('toolbar.bubbleChatEnable'), "aria-pressed": false, className: 'mobile-toolbox-chat-fab', onClick: handleToggleBubbleChat, type: 'button' },
            React.createElement("span", { className: 'mobile-toolbox-chat-fab-icon' },
                React.createElement(IconChatBubbleToggle, null),
                unreadCount > 0 && (React.createElement("span", { "aria-hidden": true, className: 'mobile-toolbox-chat-pill-dot' }))))),
        showChat && bubbleChatOpen && (React.createElement("div", { className: showAiNotes
                ? 'mobile-toolbox-chat'
                : 'mobile-toolbox-chat mobile-toolbox-chat-only' },
            !showSmileysPanel && renderedPreviews.length > 0 && (React.createElement("div", { className: 'toolbar-quick-input-previews' }, renderedPreviews.map(({ fading, message }) => (React.createElement("button", { "aria-hidden": fading, "aria-label": t('toolbar.quickChatPreviewAria', {
                    name: message.displayName,
                    message: message.message
                }), className: fading
                    ? 'toolbar-quick-input-preview toolbar-quick-input-preview-fading'
                    : 'toolbar-quick-input-preview', disabled: fading, key: message.messageId, onClick: handleOpenChat, tabIndex: fading ? -1 : 0, type: 'button' },
                React.createElement("span", { className: 'toolbar-quick-input-preview-name' }, message.displayName),
                React.createElement("span", null, truncatePreviewText(message.message))))))),
            showSmileysPanel && !smileysDisabled && (React.createElement("div", { className: 'toolbar-quick-input-smileys mobile-toolbox-chat-smileys' },
                React.createElement(SmileysPanel, { onSmileySelect: handleSmileySelect }))),
            React.createElement("div", { className: `mobile-toolbox-chat-pill${inputDisabled ? ' mobile-toolbox-chat-pill-disabled' : ''}` },
                React.createElement("button", { "aria-label": t('toolbar.bubbleChatDisable'), "aria-pressed": true, className: 'mobile-toolbox-chat-pill-btn', onClick: handleToggleBubbleChat, type: 'button' },
                    React.createElement("span", { className: 'mobile-toolbox-chat-pill-icon' },
                        React.createElement(IconChatBubbleToggle, null),
                        unreadCount > 0 && (React.createElement("span", { "aria-hidden": true, className: 'mobile-toolbox-chat-pill-dot' })))),
                React.createElement("span", { "aria-hidden": true, className: 'mobile-toolbox-chat-pill-divider' }),
                !smileysDisabled && (React.createElement("button", { "aria-label": t('toolbar.quickChatSmileysAria'), className: 'mobile-toolbox-chat-pill-btn', disabled: inputDisabled, onClick: handleToggleSmileys, onMouseDown: handlePreventMouseDown, type: 'button' },
                    React.createElement(IconFaceSmile, null))),
                React.createElement("input", { "aria-label": t('toolbar.quickChatInputAria'), autoComplete: 'off', className: 'mobile-toolbox-chat-pill-field', disabled: inputDisabled, enterKeyHint: 'send', maxLength: CHAR_LIMIT, onChange: handleInputChange, onFocus: handleInputFocus, onKeyDown: handleKeyDown, placeholder: inputDisabled
                        ? t('chat.disabled')
                        : t('toolbar.quickInputPlaceholder'), ref: inputRef, type: 'text', value: draft }),
                canSend && (React.createElement("button", { "aria-label": t('toolbar.quickChatSendAria'), className: 'mobile-toolbox-chat-pill-btn', onClick: handleSend, type: 'button' },
                    React.createElement(IconSend, null)))))),
        showAiNotes && (React.createElement("button", { "aria-label": t('toolbar.accessibilityLabel.ainotes'), className: 'mobile-toolbox-ai-fab', onClick: handleOpenAiNotes, type: 'button' },
            React.createElement("span", { className: 'mobile-toolbox-ai-fab-doc' },
                React.createElement(IconShareDoc, null)),
            React.createElement("span", { "aria-hidden": true, className: 'mobile-toolbox-ai-fab-badge' },
                React.createElement(IconAI, null))))));
};
export default MobileToolboxShortcuts;
